import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {useState} from 'react';

import {BufferConditionKeys, MeltingTempPreferences, NucleotideType} from 'biojs/BioConstants';
import {PrimerPreferences} from 'constants/PrimerConstants';
import {isFullUTFlexibilityEnabled} from 'modbio/util';
import MeltingTempParams from 'molbio/melting-temp/MeltingTempParams';
import {MELTING_TEMP_DEFAULT_ERROR_STATE} from 'molbio/melting-temp/MeltingTempUtils';
import preferences from 'Preferences';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import {Header2} from 'ui-library/Headers';
import Modal from 'ui-library/modals/Modal';

interface MeltingTempModalProps {
  isOligo?: boolean;
  nucleotideType?: NucleotideType;
  onRequestClose: () => void;
  onRequestSubmit: () => void;
}

export default function MeltingTempModal({
  isOligo,
  nucleotideType,
  onRequestClose,
  onRequestSubmit,
}: MeltingTempModalProps): JSX.Element {
  const [meltingTempOptionsDNA, setMeltingTempOptionsDNA] = useState<MeltingTempPreferences>(
    preferences.get(PrimerPreferences.MELTING_TEMP_DNA)
  );
  const [meltingTempOptionsRNA, setMeltingTempOptionsRNA] = useState<MeltingTempPreferences>(
    preferences.get(PrimerPreferences.MELTING_TEMP_RNA)
  );
  const [dnaOptionsHaveValidationError, setDNAOptionsHaveValidationError] = useState<{
    [key in BufferConditionKeys]: boolean;
  }>(MELTING_TEMP_DEFAULT_ERROR_STATE);
  const [rnaOptionsHaveValidationError, setRNAOptionsHaveValidationError] = useState<{
    [key in BufferConditionKeys]: boolean;
  }>(MELTING_TEMP_DEFAULT_ERROR_STATE);

  const compatibleBase = nucleotideType === NucleotideType.DNA ? 'T' : 'U';
  const incompatibleBase = nucleotideType === NucleotideType.DNA ? 'U' : 'T';

  return (
    <Modal
      title="Adjust Thermodynamic Parameters"
      buttons={[
        {
          text: 'Save',
          submit: true,
          disabled:
            Object.values(dnaOptionsHaveValidationError).some(
              (optionHasValidationError) => optionHasValidationError
            ) ||
            Object.values(rnaOptionsHaveValidationError).some(
              (optionHasValidationError) => optionHasValidationError
            ),
        },
      ]}
      onRequestClose={onRequestClose}
      onRequestSubmit={() => {
        preferences.set(PrimerPreferences.MELTING_TEMP_DNA, meltingTempOptionsDNA);
        preferences.set(PrimerPreferences.MELTING_TEMP_RNA, meltingTempOptionsRNA);
        onRequestSubmit();
      }}
    >
      <div className={classNames(['form-inline', css(styles.container)])}>
        <Header2 className={css(styles.header)}>DNA</Header2>
        <MeltingTempParams
          optionsHaveValidationError={dnaOptionsHaveValidationError}
          meltingTempOptions={meltingTempOptionsDNA}
          nucleotideType={NucleotideType.DNA}
          setMeltingTempOptions={setMeltingTempOptionsDNA}
          setOptionsHaveValidationError={setDNAOptionsHaveValidationError}
        />
      </div>
      <div className={classNames(['form-inline', css(styles.container)])}>
        <Header2 className={css(styles.header)}>RNA</Header2>
        <MeltingTempParams
          optionsHaveValidationError={rnaOptionsHaveValidationError}
          meltingTempOptions={meltingTempOptionsRNA}
          nucleotideType={NucleotideType.RNA}
          setMeltingTempOptions={setMeltingTempOptionsRNA}
          setOptionsHaveValidationError={setRNAOptionsHaveValidationError}
        />
      </div>
      {isOligo && nucleotideType && isFullUTFlexibilityEnabled() && (
        <InlineBanner
          bannerType={InlineBannerType.INFO}
          text={`When calculating melting temperature for a ${nucleotideType} oligo, ${incompatibleBase}s are treated as ${compatibleBase}s.`}
        />
      )}
    </Modal>
  );
}

const styles = StyleSheet.create({
  container: {
    margin: '10px',
    paddingBottom: '10px',
  },
  header: {
    paddingBottom: '5px',
  },
});
